import React, { useState, useEffect } from "react";
import axios from "axios";
import AppLayout from "../../layouts/AppLayout";
import { Users, X } from "lucide-react";

import { dataCollectionApiBaseUrl } from "../../config/api";

function formatDateTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

const Settings = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [loadingsecurity, setloadingsecurity] = useState(true);
  const [lastLogin, setLastLogin] = useState(null);
  const [countingsessions, setcountingsessions] = useState(null);
  const [loginHistoryModalOpen, setLoginHistoryModalOpen] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      setProfileLoading(false);
      setloadingsecurity(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(`${dataCollectionApiBaseUrl}/api/auth/me`, { headers })
      .then((res) => {
        if (res.data?.success && res.data.user) {
          const u = res.data.user;
          setFullName(u.name ?? "");
          setEmail(u.email ?? "");
          setPhoneNumber(u.phone ?? "");
          setLastLogin(u.lastLogin ?? null);
          setcountingsessions(res.data.countingsessions ?? 0);
        }
      })
      .catch(() => {})
      .finally(() => {
        setProfileLoading(false);
        setloadingsecurity(false);
      });
  }, []);

  const openLoginHistory = () => {
    setLoginHistoryModalOpen(true);
    setLoginHistory([]);
    setLoginHistoryLoading(true);
    axios
      .get(`${dataCollectionApiBaseUrl}/api/auth/login-history`, { headers: getAuthHeaders() })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.history)) {
          setLoginHistory(res.data.history);
        }
      })
      .catch(() => setLoginHistory([]))
      .finally(() => setLoginHistoryLoading(false));
  };

  const validate = () => {
    const next = {};
    if (!fullName?.trim()) next.fullName = "Full name is required.";
    if (!email?.trim()) next.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = "Enter a valid email.";
    if (newPassword) {
      if (!currentPassword) next.currentPassword = "Enter your current password to change it.";
      else if (newPassword.length < 6) next.newPassword = "New password must be at least 6 characters.";
      if (newPassword !== confirmNewPassword) next.confirmNewPassword = "New passwords do not match.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const updatecredentials = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setErrors({});
    setSuccess(false);
    const payload = {
      fullName: fullName.trim(),
      email: email.trim().toLowerCase(),
      phoneNumber: phoneNumber.trim() || null,
    };
    if (newPassword) {
      payload.currentPassword = currentPassword;
      payload.newPassword = newPassword;
    }
    axios
      .put(`${dataCollectionApiBaseUrl}/api/auth/me`, payload, { headers: getAuthHeaders() })
      .then((res) => {
        if (res.data?.success) {
          setSuccess(true);
          setCurrentPassword("");
          setNewPassword("");
          setConfirmNewPassword("");
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Update failed. Please try again.";
        setErrors({ submit: msg });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <AppLayout>
      <div className="p-6 bg-gray-50 min-h-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage your admin account and system preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 text-green-700">
                  <Users size={22} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Admin Profile</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Update your administrator account information</p>
                </div>
              </div>
            </div>

            <form onSubmit={updatecredentials} className="p-6">
              {profileLoading ? (
                <p className="text-sm text-gray-500 py-4">Loading profile…</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Enter full name"
                        className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-colors ${
                          errors.fullName ? "border-red-500" : "border-gray-200"
                        }`}
                      />
                      {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter email"
                        className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-colors ${
                          errors.email ? "border-red-500" : "border-gray-200"
                        }`}
                      />
                      {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="Enter phone number"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Role</label>
                      <input
                        type="text"
                        value="Admin"
                        readOnly
                        disabled
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">Change Password</h3>
                    <p className="text-sm text-gray-500 mb-4">Leave blank to keep your current password.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Current Password</label>
                        <input
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="Enter current password"
                          className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-colors ${
                            errors.currentPassword ? "border-red-500" : "border-gray-200"
                          }`}
                        />
                        {errors.currentPassword && <p className="mt-1 text-xs text-red-500">{errors.currentPassword}</p>}
                      </div>
                      <div />
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password"
                          className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-colors ${
                            errors.newPassword ? "border-red-500" : "border-gray-200"
                          }`}
                        />
                        {errors.newPassword && <p className="mt-1 text-xs text-red-500">{errors.newPassword}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm New Password</label>
                        <input
                          type="password"
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          placeholder="Confirm new password"
                          className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-colors ${
                            errors.confirmNewPassword ? "border-red-500" : "border-gray-200"
                          }`}
                        />
                        {errors.confirmNewPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmNewPassword}</p>}
                      </div>
                    </div>
                  </div>

                  {errors.submit && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700">{errors.submit}</p>
                    </div>
                  )}
                  {success && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700">Profile updated successfully.</p>
                    </div>
                  )}

                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-5 py-2.5 bg-green-700 text-white text-sm font-semibold rounded-lg hover:bg-green-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {submitting ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 text-green-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Security</h2>
              </div>
              <div className="p-6 space-y-3 text-sm">
                <div>
                  <span className="text-gray-600">Last Login</span>
                  <p className="text-gray-900 font-medium mt-0.5">
                    {loadingsecurity ? "…" : formatDateTime(lastLogin) || "—"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Active Sessions</span>
                  <p className="text-gray-900 font-medium mt-0.5">
                    {loadingsecurity ? "…" : countingsessions != null ? `${countingsessions} device${countingsessions !== 1 ? "s" : ""}` : "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openLoginHistory}
                  className="w-full mt-2 py-2 border border-green-600 text-green-700 rounded-lg text-sm font-medium hover:bg-green-50 transition-colors"
                >
                  View Login History
                </button>
              </div>
            </div>

            {loginHistoryModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/50" onClick={() => setLoginHistoryModalOpen(false)} aria-hidden="true" />
                <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Login History</h3>
                    <button
                      type="button"
                      onClick={() => setLoginHistoryModalOpen(false)}
                      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loginHistoryLoading ? (
                      <p className="text-sm text-gray-500">Loading…</p>
                    ) : loginHistory.length === 0 ? (
                      <p className="text-sm text-gray-500">No login history for this account.</p>
                    ) : (
                      <ul className="space-y-2">
                        {loginHistory.map((entry) => (
                          <li key={entry.id} className="text-sm text-gray-700 py-2 border-b border-gray-100 last:border-0">
                            {formatDateTime(entry.loginDate)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 text-green-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Email Alerts</h2>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div>
                  <p className="font-medium text-gray-900">Weekly Summary</p>
                  <p className="text-gray-500 text-xs mt-0.5">Every Monday at 8:00 AM</p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Critical Alerts</p>
                  <p className="text-gray-500 text-xs mt-0.5">Immediate delivery</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Settings;
